/**
 * Admin API 用 Zod スキーマ
 *
 * types/admin.ts の各 interface に対応するランタイムバリデーション用スキーマ。
 * 型定義は z.infer で自動生成し、interface と一致させる。
 */
import { z } from 'zod';
import { ReasoningLevelSchema } from './base-schemas';
import { TurnEndRecordSchema } from './task-end-schemas';
import { SandboxTargetSchema } from './task-schemas';
// ========================================
// Admin ドメインエンティティスキーマ
// ========================================
/**
 * 使用量の期間
 */
export const AdminUsagePeriodSchema = z.object({
    year: z.number(),
    month: z.number(),
});
/**
 * 日別使用量データ
 */
export const AdminDailyUsageDataSchema = z.object({
    pk: z.string(),
    sk: z.string(),
    date: z.string(),
    inputTokens: z.number(),
    outputTokens: z.number(),
    cacheReadTokens: z.number().optional(),
    cacheWriteTokens: z.number().optional(),
    totalTokens: z.number(),
    cost: z.number(),
    requestCount: z.number(),
    model: z.string().optional(),
});
/**
 * 月別使用量データ
 */
export const AdminMonthlyUsageDataSchema = z.object({
    pk: z.string(),
    sk: z.string(),
    month: z.string().optional(),
    inputTokens: z.number(),
    outputTokens: z.number(),
    cacheReadTokens: z.number().optional(),
    cacheWriteTokens: z.number().optional(),
    totalTokens: z.number(),
    cost: z.number(),
    requestCount: z.number(),
    model: z.string().optional(),
});
/**
 * 利用回数ランキング項目
 */
export const AdminUsageRankingItemSchema = z.object({
    email: z.string(),
    userName: z.string(),
    requestCount: z.number(),
    totalTokens: z.number(),
    cost: z.number(),
});
/**
 * ユーザー統計
 */
export const AdminUserStatsSchema = z.object({
    activeUsers: z.number(),
    totalUsers: z.number(),
    activeRate: z.number(),
});
/**
 * 利用可能なモデル情報
 */
export const AdminAvailableModelSchema = z.object({
    modelId: z.string(),
    displayName: z.string(),
});
/**
 * モデル別使用量データ（円グラフ用）
 */
export const AdminModelUsageSchema = z.object({
    modelId: z.string(),
    displayName: z.string(),
    cost: z.number(),
    requestCount: z.number(),
});
/**
 * Cognitoユーザー情報
 */
export const AdminCognitoUserSchema = z.object({
    email: z.string(),
    emailVerified: z.boolean(),
    status: z.string(),
    enabled: z.boolean(),
    createdAt: z.number().nullable(),
    /**
     * **名前に反して最終ログイン日時ではない。** 実体は Cognito の
     * `UserLastModifiedDate`（ユーザー属性が最後に更新された日時）であり、
     * アプリの起動日時でもサインイン日時でもない。画面のラベルは「最終更新」。
     *
     * 「この人が最後にいつ使ったか」を知りたい場合は、こちらではなく
     * 到達性（presence）の最終確認時刻を見ること（`presence-schemas.ts`）。
     * フィールド名の変更は保存済みデータと API 利用側の双方に及ぶため、
     * 名前は据え置いたまま意味をここに記録している。
     */
    lastLogin: z.number().nullable(),
});
/**
 * タスクサマリー（管理用）
 */
export const AdminTaskSummarySchema = z.object({
    id: z.string(),
    title: z.string(),
    userEmail: z.string(),
    createdAt: z.number(),
    updatedAt: z.number(),
    messageCount: z.number(),
    deleted: z.boolean(),
    deletedAt: z.number().optional(),
    workspaceId: z.string().optional(),
    workspaceName: z.string().optional(),
    totalTokens: z.number().optional(),
    cost: z.number().optional(),
    latestVersion: z.number().optional(),
    versionCount: z.number().optional(),
    totalCost: z.number().optional(),
    /** どの実行先で実行されたか。クラウド実行先の導入前に作られたタスクでは未設定 */
    sandboxTarget: SandboxTargetSchema.optional() /** 最新バージョンの状態（TaskStatus。境界では文字列で受ける） */,
    status: z.string().optional(),
    /** 最新バージョンの直近ターンの終了記録。導入前のタスクでは未設定 */
    lastTurnEnd: TurnEndRecordSchema.optional(),
});
/**
 * タスクバージョン情報（管理用）
 * Admin版: status は string（api-schemas 版は TaskStatusSchema）
 */
export const AdminTaskVersionInfoSchema = z.object({
    version: z.number(),
    createdAt: z.number(),
    status: z.string(),
    totalTokens: z.number().optional(),
    cost: z.number().optional() /** そのバージョンの直近ターンの終了記録 */,
    lastTurnEnd: TurnEndRecordSchema.optional(),
});
/**
 * メッセージ情報（管理用）
 */
export const AdminMessageInfoSchema = z.object({
    id: z.string(),
    role: z.string(),
    content: z.string().optional(),
    parts: z.array(z.unknown()).optional(),
    createdAt: z.number(),
    modelInfo: z
        .object({
        provider: z.string(),
        modelId: z.string(),
        /** その応答に実際に適用されたエフォート */
        reasoningLevel: ReasoningLevelSchema.optional(),
    })
        .optional(),
    usage: z
        .object({
        inputTokens: z.number(),
        outputTokens: z.number(),
        totalTokens: z.number(),
        cost: z.number().optional(),
        reasoningTokens: z.number().optional(),
        cacheReadTokens: z.number().optional(),
        cacheWriteTokens: z.number().optional(),
    })
        .optional(),
    duration: z.number().optional(),
});
/**
 * タスク詳細（管理用）
 */
export const AdminTaskDetailSchema = z.object({
    id: z.string(),
    title: z.string(),
    userEmail: z.string(),
    createdAt: z.number(),
    updatedAt: z.number(),
    deleted: z.boolean(),
    workspaceId: z.string().optional(),
    workspaceName: z.string().optional(),
    messages: z.array(AdminMessageInfoSchema),
    totalTokens: z.number().optional(),
    cost: z.number().optional(),
    /**
     * どの実行先で実行されたか。
     *
     * **業務データがどこへ送られたかの記録である。** 監査の観点ではタスクの中身と
     * 同じだけ重要であり、クラウド実行先の導入前に作られたタスクでは未設定。
     */
    sandboxTarget: SandboxTargetSchema.optional() /** 表示中のバージョンの状態（TaskStatus。境界では文字列で受ける） */,
    status: z.string().optional(),
    /** 表示中のバージョンの直近ターンの終了記録 */
    lastTurnEnd: TurnEndRecordSchema.optional(),
});
// ========================================
// Admin API レスポンススキーマ
// ========================================
/**
 * GET /admin/usage レスポンス
 */
/**
 * サンドボックス（インフラ費用）の当月使用量。
 *
 * **言語モデルの利用料とは別の費目である。** 同じ画面に並べるが合算しない。
 * 合算すると、どちらの上限で止まったのかが分からなくなる。
 * クラウド実行先が未設定の環境では未設定のまま返る。
 */
export const AdminSandboxUsageSchema = z.object({
    month: z.string(),
    /** 実行環境の稼働時間の合計（秒） */
    runtimeSeconds: z.number(),
    /** ホストと実行環境の間で運んだ量の合計（バイト） */
    transferBytes: z.number(),
    /** 当月の上限。表示側が到達度を出せるようにする */
    limits: z.object({
        runtimeSeconds: z.number(),
        transferBytes: z.number(),
    }),
});
export const GetAdminUsageResponseSchema = z.object({
    period: AdminUsagePeriodSchema,
    dailyData: z.array(AdminDailyUsageDataSchema),
    monthlyData: z.array(AdminMonthlyUsageDataSchema),
    monthlyTrend: z.array(AdminMonthlyUsageDataSchema),
    ranking: z.array(AdminUsageRankingItemSchema),
    userStats: AdminUserStatsSchema,
    availableModels: z.array(AdminAvailableModelSchema),
    modelUsage: z.array(AdminModelUsageSchema),
    /** サンドボックスの使用量（別費目として並置する）。未計上・未設定なら省略 */
    sandboxUsage: AdminSandboxUsageSchema.optional(),
});
/**
 * GET /admin/usage/user/{email} レスポンス
 */
export const GetAdminUserUsageResponseSchema = GetAdminUsageResponseSchema.extend({
    email: z.string(),
    /**
     * この利用者のサンドボックス使用量（利用者ごとの上限に対する到達度）。
     *
     * 継承元の `sandboxUsage` は全体の合計であり、利用者ページで見せると
     * 「全体はまだ余裕があるのに、この人だけ止まっている」状況が読めない。
     * 上限判定は利用者別・全体の双方を見るため、双方を並べて初めて理由が分かる。
     */
    userSandboxUsage: AdminSandboxUsageSchema.optional(),
});
/**
 * アカウント別の月間利用額（エクスポート用）の 1 行
 */
export const AdminAccountUsageItemSchema = z.object({
    email: z.string(),
    requestCount: z.number(),
    totalTokens: z.number(),
    /** 利用額（USD）。部門ごとに合計するときの誤差を避けるため丸めない */
    cost: z.number(),
});
/**
 * GET /admin/usage/users レスポンス
 *
 * ランキングと違い上位で切らない。所属部門ごとの集計に使うため、
 * その月に利用のあったアカウントを全件返す
 */
export const GetAdminAccountUsageResponseSchema = z.object({
    month: z.string(),
    users: z.array(AdminAccountUsageItemSchema),
});
/**
 * GET /admin/users レスポンス
 */
/**
 * ユーザー一覧の 1 行。
 *
 * サンドボックスの使用量を添える。**上限に達している利用者は一覧で分かる必要がある。**
 * 利用者ごとに詳細画面を開かないと分からないと、止まっている人がいることに
 * 気づけるのは本人から申告があったときだけになる。
 * クラウド実行先を使っていない利用者では計上そのものが無いため省略する。
 */
export const AdminUserListItemSchema = AdminCognitoUserSchema.extend({
    sandboxUsage: AdminSandboxUsageSchema.optional(),
    /**
     * 認証アプリ（TOTP）の登録が有効か。取得に失敗した利用者では省略する
     * （「未登録」と取り違えないよう、false にはしない）。
     */
    mfaEnabled: z.boolean().optional(),
});
export const ListAdminUsersResponseSchema = z.object({
    users: z.array(AdminUserListItemSchema),
    total: z.number(),
});
/**
 * GET /admin/tasks レスポンス
 */
export const ListAdminTasksResponseSchema = z.object({
    tasks: z.array(AdminTaskSummarySchema),
    total: z.number(),
});
/**
 * GET /admin/users/{email}/tasks/{taskId} レスポンス
 */
export const GetAdminTaskDetailResponseSchema = z.object({
    task: AdminTaskDetailSchema,
});
/**
 * GET /admin/users/{email}/tasks/{taskId}/versions レスポンス
 */
export const ListAdminTaskVersionsResponseSchema = z.object({
    versions: z.array(AdminTaskVersionInfoSchema),
});
/**
 * POST /admin/users/{email}/mfa-reset レスポンス
 *
 * 対象の利用者の TOTP 登録を解除し、全端末のサインインを失効させた結果。
 * 利用者は次回サインイン時に認証アプリの登録を求められる。
 */
export const MfaResetResponseSchema = z.object({
    email: z.string(),
    /** リセットした日時（ISO 8601） */
    resetAt: z.string(),
});
//# sourceMappingURL=admin-schemas.js.map